import React, { Suspense, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  useMultiTabDetection,
  EMultiTabState,
  ITabInfo,
} from "do-front-end-utils/hooks";
import Loader from "@/components/common/Loader/Loader";
import Routes from "@/Routes/Routes";
import { IInitialState } from "@/types/initialState.types";
import Sentry from "./utils/sentry";
import "react-tooltip/dist/react-tooltip.css";
import { DO_BASE_URL } from "./constants/AppConstants";
import AlertToast from "./components/common/AlertToast/AlertToast";
import {
  hideToast,
  multipleTabDetectedAction,
  setWhitelabelStatus,
} from "./actions/appStatus.action";
import { getPlatformMetaAction } from "./actions/platformMeta.action";
import { addFaviconInDOM, addTitleInDom } from "./utils/whitelabel";
import ErrorPage from "./components/common/ErrorPage/ErrorPage";
import {
  ATTACHMENT_PREVIEW_PARAM,
  TAB_COMMUNICATION_CHANNEL,
  TAB_ERROR_PAGE,
} from "./constants/MultiTabConstants";
import {
  infoLogger,
  EInfoSource,
  EErrorLevel,
  EErrorSource,
  errorLogger,
} from "./utils/ubaTracking.utils";
import { gracefulShutdownAction } from "./actions/auth.action";
import { getServerTimeAction } from "./actions/interview.action";

/**
 * Runs multi-tab detection and gates its children on the result: renders the
 * "Multiple Tabs Detected" page when another interview tab is found, nothing
 * while detection is still pending, and the children once it's confirmed this
 * is the only tab. Extracted from {@link App} so the hook (and its
 * BroadcastChannel emit) can be skipped entirely for attachment-preview tabs.
 */
function MultiTabBoundary({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { isMultiTab, multiTabState } = useMultiTabDetection({
    TAB_COMMUNICATION_CHANNEL,
    onMultiTabDetectionCallback: (
      eventData: ITabInfo,
      currentEmitTime: string | null,
    ) => {
      infoLogger(
        `New tab opens, session start time ${currentEmitTime}, tabId ${eventData.tabId}. Multi-tab detected.`,
        EInfoSource.MULTIPLE_TAB,
        {
          ...eventData,
          currentEmitTime,
        },
      );
    },
    onMultiTabDetectionErrorCallback: (error: unknown, event: MessageEvent) => {
      errorLogger(
        error as Error,
        EErrorLevel.CRITICAL,
        EErrorSource.MULTIPLE_TAB,
        {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          eventData: event.data,
          errorMessage: "Error in detecting multi-tab",
        },
      );
    },
    onEmitTabInfoCallback: (tabInfo: ITabInfo) => {
      infoLogger("Emitting multi tab info", EInfoSource.MULTIPLE_TAB, tabInfo);
    },
    onEmitTabInfoErrorCallback: (error: unknown) => {
      errorLogger(
        error as Error,
        EErrorLevel.CRITICAL,
        EErrorSource.MULTIPLE_TAB,
        {
          errorMessage: "Error in emiting tab info",
        },
      );
    },
    onErrorCallback: (error) => {
      errorLogger(
        error as Error,
        EErrorLevel.CRITICAL,
        EErrorSource.MULTIPLE_TAB,
      );
    },
  });

  useEffect(() => {
    if (isMultiTab) {
      dispatch(gracefulShutdownAction());
      dispatch(multipleTabDetectedAction(isMultiTab));
    }
  }, [isMultiTab]);

  if (isMultiTab) {
    return (
      <ErrorPage
        title={TAB_ERROR_PAGE.title}
        description={TAB_ERROR_PAGE.description}
      />
    );
  }

  if (multiTabState === EMultiTabState.PENDING) return null;

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

function App() {
  const dispatch = useDispatch();
  // Attachment-preview tabs (opened from within the interview) carry this flag
  // and deliberately skip multi-tab detection so they aren't mistaken for a
  // second interview tab.
  const isAttachmentPreviewTab =
    new URLSearchParams(window.location.search).get(
      ATTACHMENT_PREVIEW_PARAM,
    ) === "true";

  const {
    isLoaderEnabled,
    userIdentityData,
    toast,
    errorPage,
    platformMeta,
    whitelabelingEnabled,
    interviewSlug,
    serverTime,
  } = useSelector(
    (state: IInitialState) => ({
      isLoaderEnabled: state.progress?.apiProgressStatus?.enabled,
      userIdentityData: state.auth.userIdentityData,
      toast: state.appStatus.toast,
      errorPage: state.appStatus.errorPage,
      platformMeta: state.platformMeta,
      whitelabelingEnabled: state.appStatus.whitelabelingEnabled,
      interviewSlug: state.interview?.slug,
      serverTime: state.serverTime,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (!serverTime.serverTime) {
      dispatch(getServerTimeAction());
    }
  }, [serverTime.serverTime]);

  useEffect(() => {
    if (whitelabelingEnabled === null) {
      dispatch(setWhitelabelStatus(window.location.origin !== DO_BASE_URL));
    } else if (whitelabelingEnabled && !platformMeta && interviewSlug) {
      dispatch(getPlatformMetaAction(interviewSlug));
    } else if (whitelabelingEnabled === false) {
      addFaviconInDOM("/do-favicon.ico");
      addTitleInDom("DoSelect");
    } else if (platformMeta) {
      addFaviconInDOM(platformMeta.short_logo_url);
      addTitleInDom(platformMeta.name);
    }
  }, [interviewSlug, whitelabelingEnabled, platformMeta]);

  useEffect(() => {
    if (userIdentityData?.user_id) {
      Sentry.setUser({
        email: userIdentityData.email,
        full_name: userIdentityData.name,
        user_name: userIdentityData.username,
        user_role: userIdentityData.role,
      });
    }
  }, [userIdentityData?.user_id]);

  if (errorPage) {
    return (
      <ErrorPage title={errorPage.title} description={errorPage.description} />
    );
  }

  return (
    <Suspense fallback={<Loader isFullScreen />}>
      {/* Attachment-preview tabs skip detection entirely; every other tab is
          gated by MultiTabBoundary (which renders the routes once it confirms
          this is the only interview tab). */}
      {isAttachmentPreviewTab ? (
        <Routes />
      ) : (
        <MultiTabBoundary>
          <Routes />
        </MultiTabBoundary>
      )}
      {isLoaderEnabled && <Loader isFullScreen />}
      {toast?.enable && (
        <AlertToast
          message={toast.data.message}
          onDismissCallBack={() => {
            if (toast.data.onDismissCallBack) toast.data.onDismissCallBack();
            else dispatch(hideToast());
          }}
          type={toast.data.type}
          autoHide={toast.data.autoHide}
        />
      )}
    </Suspense>
  );
}

export default App;
